import React, { useEffect, useMemo, useState } from "react";
import Layout, { PageHeader } from "../components/Layout";
import api, { formatApiError } from "../lib/api";
import StatusBadge from "../components/StatusBadge";
import BulkDnsModal from "../components/BulkDnsModal";
import CloudflareOnboardingModal, { CfStatusBadge } from "../components/CloudflareOnboardingModal";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { ArrowsClockwise, MagnifyingGlass, LockKey, LockKeyOpen, ShieldCheck, Copy, ArrowSquareOut, Export, CloudArrowUp } from "@phosphor-icons/react";

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" }); }
  catch { return iso; }
}
function daysUntil(iso) {
  if (!iso) return null;
  try {
    const diff = (new Date(iso).getTime() - Date.now()) / 86400000;
    return Math.ceil(diff);
  } catch { return null; }
}

export default function Domains() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastSynced, setLastSynced] = useState(null);
  const [search, setSearch] = useState("");
  const [tldFilter, setTldFilter] = useState("all");
  const [dnsFilter, setDnsFilter] = useState("all");
  const [expiryFilter, setExpiryFilter] = useState("all");
  const [sort, setSort] = useState("newest");
  const [selected, setSelected] = useState(new Set());
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const [modalOpen, setModalOpen] = useState(false);
  const [cfZones, setCfZones] = useState({});
  const [cfOnboardDomain, setCfOnboardDomain] = useState(null);
  const [cfModalOpen, setCfModalOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [domainsRes, cfRes] = await Promise.all([
        api.get("/domains"),
        api.get("/cloudflare/zones").catch(() => ({ data: { zones: {} } })),
      ]);
      setItems(domainsRes.data.items || []);
      setLastSynced(domainsRes.data.last_synced);
      setCfZones(cfRes.data.zones || {});
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  const refreshFromNamecheap = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/domains/refresh");
      toast.success(`Synced ${data.count} domains`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setRefreshing(false); }
  };

  useEffect(() => { load(); }, []);

  const tlds = useMemo(() => {
    const set = new Set();
    items.forEach((d) => { const t = d.name?.split(".").slice(1).join("."); if (t) set.add(t); });
    return Array.from(set).sort();
  }, [items]);

  const filtered = useMemo(() => {
    let rows = [...items];
    const q = search.trim().toLowerCase();
    if (q) rows = rows.filter((d) => (d.name || "").toLowerCase().includes(q));
    if (tldFilter !== "all") rows = rows.filter((d) => d.name?.endsWith("." + tldFilter));
    if (dnsFilter !== "all") rows = rows.filter((d) => d.dns_type === dnsFilter);
    if (expiryFilter !== "all") {
      rows = rows.filter((d) => {
        const du = daysUntil(d.expiry_date);
        if (expiryFilter === "expired") return du !== null && du < 0;
        if (expiryFilter === "expiring") return du !== null && du >= 0 && du <= 30;
        if (expiryFilter === "active") return du === null || du > 30;
        return true;
      });
    }
    rows.sort((a, b) => {
      switch (sort) {
        case "oldest": return (a.created_date || "").localeCompare(b.created_date || "");
        case "az": return (a.name || "").localeCompare(b.name || "");
        case "za": return (b.name || "").localeCompare(a.name || "");
        case "expiry_soon": return (a.expiry_date || "").localeCompare(b.expiry_date || "");
        case "expiry_late": return (b.expiry_date || "").localeCompare(a.expiry_date || "");
        case "newest":
        default:
          return (b.created_date || "").localeCompare(a.created_date || "");
      }
    });
    return rows;
  }, [items, search, tldFilter, dnsFilter, expiryFilter, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = useMemo(
    () => filtered.slice((page - 1) * pageSize, page * pageSize),
    [filtered, page]
  );

  useEffect(() => { setPage(1); }, [search, tldFilter, dnsFilter, expiryFilter, sort]);

  const toggleAll = () => {
    const ids = paged.map((d) => d.name);
    const allSel = ids.every((id) => selected.has(id));
    const next = new Set(selected);
    ids.forEach((id) => { if (allSel) next.delete(id); else next.add(id); });
    setSelected(next);
  };

  const toggleOne = (name) => {
    const next = new Set(selected);
    if (next.has(name)) next.delete(name); else next.add(name);
    setSelected(next);
  };

  const exportCsv = () => {
    const rows = ["domain,created_date,expiry_date,dns_type,auto_renew,is_locked,cf_status"];
    filtered.forEach((d) => rows.push([
      d.name, d.created_date || "", d.expiry_date || "", d.dns_type || "",
      d.auto_renew, d.is_locked,
      cfZones[d.name]?.onboarding_status || "not_onboarded",
    ].join(",")));
    const blob = new Blob([rows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "domains.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Layout>
      <PageHeader
        eyebrow="Portfolio"
        title="Domains"
        description={lastSynced ? `Last synced ${new Date(lastSynced).toLocaleString()}` : "Sync from Namecheap to load your portfolio."}
        actions={
          <>
            <Button variant="outline" onClick={exportCsv} data-testid="export-csv-btn">
              <Export size={16} className="mr-2" /> CSV
            </Button>
            <Button onClick={refreshFromNamecheap} disabled={refreshing} data-testid="sync-btn">
              <ArrowsClockwise size={16} className={`mr-2 ${refreshing ? "animate-spin" : ""}`} />
              {refreshing ? "Syncing…" : "Sync"}
            </Button>
          </>
        }
      />

      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-[1fr_repeat(4,160px)] gap-2 mb-4">
        <div className="relative">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search domains…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 font-mono text-sm"
            data-testid="search-input"
          />
        </div>
        <Select value={tldFilter} onValueChange={setTldFilter}>
          <SelectTrigger data-testid="tld-filter"><SelectValue placeholder="TLD" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All TLDs</SelectItem>
            {tlds.map((t) => <SelectItem key={t} value={t}>.{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={dnsFilter} onValueChange={setDnsFilter}>
          <SelectTrigger data-testid="dns-filter"><SelectValue placeholder="DNS" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All DNS</SelectItem>
            <SelectItem value="namecheap">Namecheap DNS</SelectItem>
            <SelectItem value="custom">Custom DNS</SelectItem>
          </SelectContent>
        </Select>
        <Select value={expiryFilter} onValueChange={setExpiryFilter}>
          <SelectTrigger data-testid="expiry-filter"><SelectValue placeholder="Expiry" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="expiring">Expiring ≤ 30d</SelectItem>
            <SelectItem value="expired">Expired</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger data-testid="sort-select"><SelectValue placeholder="Sort" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="newest">Newest first</SelectItem>
            <SelectItem value="oldest">Oldest first</SelectItem>
            <SelectItem value="az">A → Z</SelectItem>
            <SelectItem value="za">Z → A</SelectItem>
            <SelectItem value="expiry_soon">Expiry soonest</SelectItem>
            <SelectItem value="expiry_late">Expiry latest</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div
          className="mb-3 flex items-center justify-between px-4 py-2 bg-primary/10 border border-primary/30 rounded-md sticky top-14 z-20"
          data-testid="bulk-action-bar"
        >
          <div className="text-sm font-medium">
            <span className="font-display font-black">{selected.size}</span> selected
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setSelected(new Set())} data-testid="clear-selection-btn">
              Clear
            </Button>
            <Button size="sm" onClick={() => setModalOpen(true)} data-testid="bulk-dns-btn">
              Bulk DNS Change
            </Button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="border border-border rounded-md overflow-hidden bg-card">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="domains-table">
            <thead className="bg-muted/40">
              <tr className="text-left">
                <th className="px-4 py-3 w-10">
                  <Checkbox
                    checked={paged.length > 0 && paged.every((d) => selected.has(d.name))}
                    onCheckedChange={toggleAll}
                    data-testid="select-all"
                  />
                </th>
                <th className="px-4 py-3 overline">Domain</th>
                <th className="px-4 py-3 overline">Created</th>
                <th className="px-4 py-3 overline">Expires</th>
                <th className="px-4 py-3 overline">DNS</th>
                <th className="px-4 py-3 overline">Cloudflare</th>
                <th className="px-4 py-3 overline">Auto-renew</th>
                <th className="px-4 py-3 overline">Lock</th>
                <th className="px-4 py-3 overline">Privacy</th>
                <th className="px-4 py-3 overline text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={10} className="px-4 py-10 text-center text-muted-foreground">Loading…</td></tr>
              ) : paged.length === 0 ? (
                <tr>
                  <td colSpan={10} className="px-4 py-16 text-center">
                    <div className="overline mb-2">No Domains</div>
                    <div className="text-sm text-muted-foreground">
                      Configure Namecheap credentials in Settings and click Sync.
                    </div>
                  </td>
                </tr>
              ) : paged.map((d) => {
                const du = daysUntil(d.expiry_date);
                return (
                  <tr
                    key={d.name}
                    className="border-t border-border table-row-hover"
                    data-testid={`domain-row-${d.name}`}
                  >
                    <td className="px-4 py-3">
                      <Checkbox
                        checked={selected.has(d.name)}
                        onCheckedChange={() => toggleOne(d.name)}
                        data-testid={`select-${d.name}`}
                      />
                    </td>
                    <td className="px-4 py-3 font-mono text-sm font-medium">{d.name}</td>
                    <td className="px-4 py-3 text-xs text-muted-foreground font-mono">{fmtDate(d.created_date)}</td>
                    <td className="px-4 py-3 text-xs font-mono">
                      {fmtDate(d.expiry_date)}
                      {du !== null && du >= 0 && du <= 30 && (
                        <StatusBadge variant="warning" className="ml-2">{du}d</StatusBadge>
                      )}
                      {du !== null && du < 0 && (
                        <StatusBadge variant="danger" className="ml-2">expired</StatusBadge>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge variant={d.dns_type === "custom" ? "info" : "default"}>
                        {d.dns_type === "custom" ? "Custom" : "Namecheap"}
                      </StatusBadge>
                    </td>
                    <td className="px-4 py-3" data-testid={`cf-status-${d.name}`}>
                      <CfStatusBadge status={cfZones[d.name]?.onboarding_status || null} />
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge variant={d.auto_renew ? "success" : "default"}>
                        {d.auto_renew ? "On" : "Off"}
                      </StatusBadge>
                    </td>
                    <td className="px-4 py-3">
                      {d.is_locked
                        ? <LockKey size={14} className="text-success" weight="fill" />
                        : <LockKeyOpen size={14} className="text-muted-foreground" />}
                    </td>
                    <td className="px-4 py-3">
                      {(d.whois_guard && d.whois_guard !== "NOTPRESENT")
                        ? <ShieldCheck size={14} className="text-success" weight="fill" />
                        : <span className="text-muted-foreground text-xs">—</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          className="h-7 w-7 rounded hover:bg-muted grid place-items-center"
                          onClick={() => { navigator.clipboard.writeText(d.name); toast.success("Copied"); }}
                          data-testid={`copy-${d.name}`}
                          title="Copy domain"
                        >
                          <Copy size={14} />
                        </button>
                        <a
                          className="h-7 w-7 rounded hover:bg-muted grid place-items-center"
                          href={`https://${d.name}`} target="_blank" rel="noreferrer"
                          data-testid={`open-${d.name}`}
                          title="Open in new tab"
                        >
                          <ArrowSquareOut size={14} />
                        </a>
                        <button
                          className={`h-7 w-7 rounded grid place-items-center transition-colors ${
                            cfZones[d.name]
                              ? "hover:bg-primary/10 text-primary"
                              : "hover:bg-muted text-muted-foreground"
                          }`}
                          onClick={() => { setCfOnboardDomain(d.name); setCfModalOpen(true); }}
                          data-testid={`cf-onboard-${d.name}`}
                          title={cfZones[d.name] ? "Manage Cloudflare" : "Add to Cloudflare"}
                        >
                          <CloudArrowUp size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex items-center justify-between mt-4 text-sm">
        <div className="text-muted-foreground text-xs font-mono">
          {filtered.length > 0 && `Showing ${(page - 1) * pageSize + 1}–${Math.min(page * pageSize, filtered.length)} of ${filtered.length}`}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="prev-page">Prev</Button>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="next-page">Next</Button>
        </div>
      </div>

      <BulkDnsModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        domains={Array.from(selected)}
        onCompleted={() => { setSelected(new Set()); load(); }}
      />
      <CloudflareOnboardingModal
        open={cfModalOpen}
        onClose={() => { setCfModalOpen(false); setCfOnboardDomain(null); }}
        domain={cfOnboardDomain}
        onCompleted={() => {
          // Refresh CF zones
          api.get("/cloudflare/zones").then((r) => setCfZones(r.data.zones || {})).catch(() => {});
        }}
      />
    </Layout>
  );
}
